/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRIdentifier } from "../datatypeComponents/IdentifierEntry";
import PeriodEntry, { displayStringFromFHIRPeriod } from "../datatypeComponents/PeriodEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import { DisplayFromFHIRCodeableReference } from "../datatypeComponents/CodeableReferenceEntry";
import { DisplayFromFHIRRelatedArtifact } from "../datatypeComponents/RelatedArtifactEntry";
import { DisplayFromFHIRRelatesTo } from "../datatypeComponents/RelatesToEntry";
import { DisplayFromFHIRAnnotation } from "../datatypeComponents/AnnotationEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import Spinner from "../components/Spinner";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import SEVCO, { statisticTypeCodeableConceptSet, studyDesignCodeableConceptSet } from "../dictionaries/SEVCO";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import UnsignedIntEntry from "../datatypeComponents/UnsignedIntEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import BooleanEntry from "../datatypeComponents/BooleanEntry";
import IdEntry from "../datatypeComponents/IdEntry";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import generateNarrativeForResearchStudyResource from "./generateNarrativeFunctions/generateNarrativeForResearchStudyResource";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { languageCodeDisplay, languageCodes } from "../dictionaries/CodeSystemLookup";
import CodeEntry from "../datatypeComponents/CodeEntry";

const focusReferenceResourceTypes = ['Medication', 'MedicinalProductDefinition', 'SubstanceDefinition', 'EvidenceVariable'];
const researchStudyDotPhaseValueSet = [
    { system: "http://hl7.org/fhir/research-study-phase", code: "n-a", display: "N/A" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "early-phase-1", display: "Early Phase 1" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-1", display: "Phase 1" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-1-phase-2", display: "Phase 1/Phase 2" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-2", display: "Phase 2" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-2-phase-3", display: "Phase 2/Phase 3" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-3", display: "Phase 3" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-4", display: "Phase 4" }
];
const researchStudyDotPrimaryPurposeTypeValueSet = [
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "treatment", display: "Treatment" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "prevention", display: "Prevention" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "diagnostic", display: "Diagnostic" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "supportive-care", display: "Supportive Care" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "screening", display: "Screening" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "health-services-research", display: "Health Services Research" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "basic-science", display: "Basic Science" },
    { system: "http://hl7.org/fhir/research-study-prim-purp-type", code: "device-feasibility", display: "Device Feasibility" }
];
const researchStudyDotWhyStoppedValueSet = [
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "accrual-goal-met", display: "Accrual Goal Met" },
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "closed-due-to-toxicity", display: "Closed due to toxicity" },
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "closed-due-to-lack-of-study-progress", display: "Closed due to lack of study progress" },
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "temporarily-closed-per-study-design", display: "Temporarily closed per study design" }
];
const resultReferenceResourceTypes = ['Citation', 'Composition', 'Evidence', 'DiagnosticReport'];
const siteReferenceResourceTypes = ['Location', 'ResearchStudy', 'Organization'];

const researchStudyDotAssociatedPartyRoleValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "sponsor", display: "Sponsor" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "lead-sponsor", display: "Lead sponsor" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "sponsor-investigator", display: "Sponsor-investigator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "primary-investigator", display: "Principal investigator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "collaborator", display: "Collaborator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "funding-source", display: "Funding source" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "general-contact", display: "General contact" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "recruitment-contact", display: "Recruitment contact" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "sub-investigator", display: "Sub-investigator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "study-chair", display: "Study chair" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "irb", display: "Institutional Review Board" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "data-monitoring", display: "Data Monitoring" }
];
const ResearchStudyAssociatedPartyEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [partyEntryState, setPartyEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingPartyEntry = {
            name: "", role: "", period: [], classifier: [], party: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingPartyEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingPartyEntry.name = startingValue.name; }
            if (startingValue.role) { startingPartyEntry.role = startingValue.role; }
            if (startingValue.period) { startingPartyEntry.period = startingValue.period; }
            if (startingValue.classifier) { startingPartyEntry.classifier = startingValue.classifier; }
            if (startingValue.party) { startingPartyEntry.party = startingValue.party; }
        }
        setPartyEntryState(JSON.parse(JSON.stringify(startingPartyEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(partyEntryState).length > 0) {
            let newPartyEntry = {};
            if (partyEntryState.extension) { newPartyEntry.extension = partyEntryState.extension; }
            if (partyEntryState.name) { newPartyEntry.name = partyEntryState.name; }
            if (partyEntryState.role && Object.keys(partyEntryState.role).length > 0) {
                newPartyEntry.role = partyEntryState.role;
            }
            if (Array.isArray(partyEntryState.period) && partyEntryState.period.length > 0) {
                newPartyEntry.period = partyEntryState.period;
            }
            if (Array.isArray(partyEntryState.classifier) && partyEntryState.classifier.length > 0) {
                newPartyEntry.classifier = partyEntryState.classifier;
            }
            if (partyEntryState.party && Object.keys(partyEntryState.party).length > 0) {
                newPartyEntry.party = partyEntryState.party;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newPartyEntry }; });
        }
    }), [partyEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.role && <span><b>Role: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.role} /><br /></span>}
                {Array.isArray(startingValue.period) &&
                    startingValue.period.map((period, periodIndex) => {
                        return <div key={periodIndex}><b>Period: </b>{displayStringFromFHIRPeriod(period)}</div>
                    })}
                {Array.isArray(startingValue.classifier) &&
                    startingValue.classifier.map((classifier, classifierIndex) => {
                        return <div key={classifierIndex}><b>Classifier: </b><DisplayFromFHIRCodeableConcept codeableConcept={classifier} /></div>
                    })}
                {startingValue.party && <div><b>Party: </b><DisplayFromFHIRReference reference={startingValue.party} /><br /></div>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(partyEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={partyEntryState.name} setResourceState={setPartyEntryState} />
                <CodeableConceptEntry elementName='role' fieldLabel='Role'
                    valueSet={researchStudyDotAssociatedPartyRoleValueSet} startCollapsed={startCollapsed}
                    startingValue={partyEntryState.role} setResourceState={setPartyEntryState} />
                <ArrayEntry datatype='Period' elementName='period' fieldLabel='Period'
                    startCollapsed={startCollapsed}
                    startingValue={partyEntryState.period} setResourceState={setPartyEntryState} />
                <ArrayEntry datatype='CodeableConcept' elementName='classifier' fieldLabel='Classifier'
                    startCollapsed={startCollapsed}
                    startingValue={partyEntryState.classifier} setResourceState={setPartyEntryState} />
                <ReferenceEntry elementName='party' fieldLabel='Party (as Reference)'
                    referencedResourceTypes={['Practitioner', 'PractitionerRole', 'Organization']}
                    startingValue={partyEntryState.party} setResourceState={setPartyEntryState} />
            </div>
        </>
    }
}

const ResearchStudyComparisonGroupEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [comparisonGroupEntryState, setComparisonGroupEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingComparisonGroupEntry = {
            targetNumber: "", actualNumber: "", eligibility: "", observedGroup: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingComparisonGroupEntry.extension = startingValue.extension; }
            if (startingValue.targetNumber || startingValue.targetNumber === 0) { startingComparisonGroupEntry.targetNumber = startingValue.targetNumber; }
            if (startingValue.actualNumber || startingValue.actualNumber === 0) { startingComparisonGroupEntry.actualNumber = startingValue.actualNumber; }
            if (startingValue.eligibility) {
                startingComparisonGroupEntry.eligibility = startingValue.eligibility;
            } else {
                let newEligibility = {};
                let newEligibilityDisplay = "";
                if (startingValue.name) { newEligibilityDisplay = startingValue.name; }
                if (startingValue.type) { newEligibilityDisplay += " " + displayStringFromFHIRCodeableConcept(startingValue.type); }
                if (startingValue.description) { newEligibilityDisplay += " " + startingValue.description; }
                if (newEligibilityDisplay) {
                    newEligibility.display = newEligibilityDisplay;
                    startingComparisonGroupEntry.eligibility = newEligibility;
                }
                if (Array.isArray(startingValue.intendedExposure) && startingValue.intendedExposure.length) {
                    if (!startingComparisonGroupEntry.extension) {
                        startingComparisonGroupEntry.extension = [];
                    }
                    for (const instance of startingValue.intendedExposure) {
                        startingComparisonGroupEntry.extension.push({
                            "url": "http://hl7.org/fhir/5.0/StructureDefinition/extension-ResearchStudy.comparisonGroup.intendedExposure",
                            "valueReference": instance
                        })
                    }
                }
            }
            if (startingValue.observedGroup) { startingComparisonGroupEntry.observedGroup = startingValue.observedGroup; }
        }
        setComparisonGroupEntryState(JSON.parse(JSON.stringify(startingComparisonGroupEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(comparisonGroupEntryState).length > 0) {
            let newGroupEntry = {};
            if (comparisonGroupEntryState.extension) { newGroupEntry.extension = comparisonGroupEntryState.extension; }
            if (comparisonGroupEntryState.targetNumber || comparisonGroupEntryState.targetNumber === 0) {
                newGroupEntry.targetNumber = comparisonGroupEntryState.targetNumber;
            }
            if (comparisonGroupEntryState.actualNumber || comparisonGroupEntryState.actualNumber === 0) {
                newGroupEntry.actualNumber = comparisonGroupEntryState.actualNumber;
            }
            if (comparisonGroupEntryState.eligibility && Object.keys(comparisonGroupEntryState.eligibility).length > 0) {
                newGroupEntry.eligibility = comparisonGroupEntryState.eligibility;
            }
            if (comparisonGroupEntryState.observedGroup && Object.keys(comparisonGroupEntryState.observedGroup).length > 0) {
                newGroupEntry.observedGroup = comparisonGroupEntryState.observedGroup;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newGroupEntry }; });
        }
    }), [comparisonGroupEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.linkId && <p><b>Id: </b>{startingValue.linkId}</p>}
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.type} /><br /></span>}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.targetNumber && <p><b>Target number of participants: </b>{startingValue.targetNumber}</p>}
                {(startingValue.actualNumber || startingValue.actualNumber === 0) && <p><b>Actual number of participants: </b>{startingValue.actualNumber}</p>}
                {startingValue.eligibility && <div><b>Eligibility Criteria: </b><DisplayFromFHIRReference reference={startingValue.eligibility} /><br /></div>}
                {Array.isArray(startingValue.intendedExposure) &&
                    startingValue.intendedExposure.map((intended, intendedIndex) => {
                        return <div key={intendedIndex}><b>Exposure: </b><DisplayFromFHIRReference reference={intended} /></div>
                    })}
                {startingValue.observedGroup && <span><b>Observed Group: </b><DisplayFromFHIRReference reference={startingValue.observedGroup} /><br /></span>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(comparisonGroupEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <UnsignedIntEntry elementName='targetNumber' fieldLabel='Target number of participants'
                    startingValue={comparisonGroupEntryState.targetNumber} setResourceState={setComparisonGroupEntryState} />
                <UnsignedIntEntry elementName='actualNumber' fieldLabel='Actual number of participants'
                    startingValue={comparisonGroupEntryState.actualNumber} setResourceState={setComparisonGroupEntryState} />
                <ReferenceEntry
                    elementName='eligibility' fieldLabel='Eligibility Criteria' startCollapsed={startCollapsed}
                    referencedResourceTypes={['Group']} enableCreation={true}
                    startingValue={comparisonGroupEntryState.eligibility} setResourceState={setComparisonGroupEntryState} />
                <ReferenceEntry elementName='observedGroup' fieldLabel='Observed Group'
                    referencedResourceTypes={['Group']} startCollapsed={startCollapsed}
                    startingValue={comparisonGroupEntryState.observedGroup} setResourceState={setComparisonGroupEntryState} />
            </div>
        </>
    }
}

const researchStudyDotLabelValueSet = [
    { system: "http://hl7.org/fhir/title-type", code: "primary", display: "Primary title" },
    { system: "http://hl7.org/fhir/title-type", code: "official", display: "Official title" },
    { system: "http://hl7.org/fhir/title-type", code: "scientific", display: "Scientific title" },
    { system: "http://hl7.org/fhir/title-type", code: "plain-language", display: "Plain language title" },
    { system: "http://hl7.org/fhir/title-type", code: "subtitle", display: "Subtitle" },
    { system: "http://hl7.org/fhir/title-type", code: "short-title", display: "Short title" },
    { system: "http://hl7.org/fhir/title-type", code: "acronym", display: "Acronym" },
    { system: "http://hl7.org/fhir/title-type", code: "earlier-title", display: "Different text in an earlier version" },
    { system: "http://hl7.org/fhir/title-type", code: "language", display: "Different language" },
    { system: "http://hl7.org/fhir/title-type", code: "autotranslated", display: "Different language derived from autotranslation" },
    { system: "http://hl7.org/fhir/title-type", code: "human-use", display: "Human use" },
    { system: "http://hl7.org/fhir/title-type", code: "machine-use", display: "Machine use" },
    { system: "http://hl7.org/fhir/title-type", code: "duplicate-uid", display: "Different text for the same object with a different identifier" }
];
const ResearchStudyLabelEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [labelEntryState, setLabelEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingLabelEntry = {
            "type": "", "language": "", "value": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingLabelEntry.extension = startingValue.extension; }
            if (startingValue.type) { startingLabelEntry.type = startingValue.type; }
            if (startingValue.language) { startingLabelEntry.language = startingValue.language; }
            if (startingValue.value) { startingLabelEntry.value = startingValue.value; }
        }
        setLabelEntryState(startingLabelEntry);
    }, []);

    useEffect((() => {
        if (Object.keys(labelEntryState).length > 0) {
            let newLabelEntry = {};
            if (labelEntryState.extension) { newLabelEntry.extension = labelEntryState.extension; }
            if (labelEntryState.type && Object.keys(labelEntryState.type).length > 0) {
                newLabelEntry.type = labelEntryState.type;
            }
            if (labelEntryState.language) { newLabelEntry.language = labelEntryState.language; }
            if (labelEntryState.value) { newLabelEntry.value = labelEntryState.value; }
            setResourceState(prevState => { return { ...prevState, [elementName]: newLabelEntry }; });
        }
    }), [labelEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <p>
                <b>{simpleDisplayStringFromFHIRCodeableConcept(startingValue.type) ||
                    "Label"}{startingValue.language &&
                        ", Language -- " + languageCodeDisplay[startingValue.language]}: </b>{startingValue.value}
            </p>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(labelEntryState).length > 0) {
        return <>
            {labelEntryState !== undefined && <>
                <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
                <div style={{ marginLeft: "24px" }}>
                    <StringEntry elementName='value' fieldLabel='Label value'
                        startingValue={labelEntryState.value} setResourceState={setLabelEntryState} />
                    <CodeableConceptEntry elementName='type' fieldLabel='Label type'
                        valueSet={researchStudyDotLabelValueSet} startCollapsed={startCollapsed}
                        startingValue={labelEntryState.type} setResourceState={setLabelEntryState} />
                    <CodeEntry elementName='language' fieldLabel='Language'
                        startingValue={labelEntryState.language} setResourceState={setLabelEntryState}
                        allowedValues={languageCodes} />
                </div>
            </>}
        </>
    }
}

const EventHandlingEntry = ({ elementName, fieldLabel, startingValue, setResourceState }) => {

    const [eventHandlingState, setEventHandlingState] = useState({});

    useEffect(() => {
        let startingEventHandlingEntry = {
            event: "", group: "", handling: "", description: ""
        }
        if (startingValue) {
            if (startingValue.extension) {
                startingEventHandlingEntry.extension = startingValue.extension;
            }
            if (startingValue.event) {
                startingEventHandlingEntry.event = startingValue.event;
            }
            if (startingValue.group) {
                startingEventHandlingEntry.group = startingValue.group;
            }
            if (startingValue.handling) {
                startingEventHandlingEntry.handling = startingValue.handling;
            }
            if (startingValue.description) {
                startingEventHandlingEntry.description = startingValue.description;
            }
        }
        setEventHandlingState(JSON.parse(JSON.stringify(startingEventHandlingEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(eventHandlingState).length > 0) {
            let newEventHandling = {};
            if (eventHandlingState.extension) { newEventHandling.extension = eventHandlingState.extension; }
            if (eventHandlingState.event && Object.keys(eventHandlingState.event).length > 0) {
                newEventHandling.event = eventHandlingState.event;
            }
            if (eventHandlingState.group && Object.keys(eventHandlingState.group).length > 0) {
                newEventHandling.group = eventHandlingState.group;
            }
            if (eventHandlingState.handling && Object.keys(eventHandlingState.handling).length > 0) {
                newEventHandling.handling = eventHandlingState.handling;
            }
            if (eventHandlingState.description && Object.keys(eventHandlingState.description).length > 0) {
                newEventHandling.description = eventHandlingState.description;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEventHandling }; });
        }
    }), [eventHandlingState]);

    if (Object.keys(eventHandlingState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='event' fieldLabel='Event'
                    startingValue={eventHandlingState.event} setResourceState={setEventHandlingState} />
                <CodeableConceptEntry elementName='group' fieldLabel='Group'
                    startingValue={eventHandlingState.group} setResourceState={setEventHandlingState} />
                <CodeableConceptEntry elementName='handling' fieldLabel='Handling'
                    startingValue={eventHandlingState.handling} setResourceState={setEventHandlingState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startingValue={eventHandlingState.description} setResourceState={setEventHandlingState} />
            </div>
        </>
    }
}

const StudyAmendmentEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [studyAmendmentState, setStudyAmendmentState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingStudyAmendment = {
            details: "",
            scope: "",
            description: "",
            rationale: "",
            substantialImpactSafety: "",
            substantialImpactSafetyComment: "",
            substantialImpactReliability: "",
            substantialImpactReliabilityComment: "",
            primaryReason: ""
        }
        if (startingValue?.extension) {
            for (const ext of startingValue.extension) {
                if (ext.url === "details") {
                    startingStudyAmendment.details = ext.valueString;
                } else if (ext.url === "scope") {
                    startingStudyAmendment.scope = ext.valueString;
                } else if (ext.url === "description") {
                    startingStudyAmendment.description = ext.valueString;
                } else if (ext.url === "rationale") {
                    startingStudyAmendment.rationale = ext.valueString;
                } else if (ext.url === "substantialImpactSafety") {
                    startingStudyAmendment.substantialImpactSafety = ext.valueBoolean;
                } else if (ext.url === "substantialImpactSafetyComment") {
                    startingStudyAmendment.substantialImpactSafetyComment = ext.valueString;
                } else if (ext.url === "substantialImpactReliability") {
                    startingStudyAmendment.substantialImpactReliability = ext.valueBoolean;
                } else if (ext.url === "substantialImpactReliabilityComment") {
                    startingStudyAmendment.substantialImpactReliabilityComment = ext.valueString;
                } else if (ext.url === "primaryReason") {
                    startingStudyAmendment.primaryReason = ext.valueCodeableConcept;
                }
            }
        }
        setStudyAmendmentState(JSON.parse(JSON.stringify(startingStudyAmendment)));
    }, []);

    useEffect((() => {
        if (Object.keys(studyAmendmentState).length > 0) {
            let newStudyAmendment = {
                extension: [], url: "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-study-amendment"
            };
            if (studyAmendmentState.details) {
                newStudyAmendment.extension.push({ "url": "details", "valueString": studyAmendmentState.details });
            }
            if (studyAmendmentState.scope) {
                newStudyAmendment.extension.push({ "url": "scope", "valueId": studyAmendmentState.scope });
            }
            if (studyAmendmentState.description) {
                newStudyAmendment.extension.push({ "url": "description", "valueString": studyAmendmentState.description });
            }
            if (studyAmendmentState.rationale) {
                newStudyAmendment.extension.push({ "url": "rationale", "valueString": studyAmendmentState.rationale });
            }
            if (studyAmendmentState.substantialImpactSafety === true || studyAmendmentState.substantialImpactSafety === false) {
                newStudyAmendment.extension.push({ "url": "substantialImpactSafety", "valueBoolean": studyAmendmentState.substantialImpactSafety });
            }
            if (studyAmendmentState.substantialImpactSafetyComment) {
                newStudyAmendment.extension.push({ "url": "substantialImpactSafetyComment", "valueString": studyAmendmentState.substantialImpactSafetyComment });
            }
            if (studyAmendmentState.substantialImpactReliability === true || studyAmendmentState.substantialImpactReliability === false) {
                newStudyAmendment.extension.push({ "url": "substantialImpactReliability", "valueBoolean": studyAmendmentState.substantialImpactReliability });
            }
            if (studyAmendmentState.substantialImpactReliabilityComment) {
                newStudyAmendment.extension.push({ "url": "substantialImpactReliabilityComment", "valueString": studyAmendmentState.substantialImpactReliabilityComment });
            }
            if (studyAmendmentState.primaryReason) {
                newStudyAmendment.extension.push({ "url": "primaryReason", "valueCodeableConcept": studyAmendmentState.primaryReason });
            }
            if (newStudyAmendment.extension.length) {
                setResourceState(prevState => { return { ...prevState, [elementName]: newStudyAmendment }; });
            }
        }
    }), [studyAmendmentState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {studyAmendmentState.details && <p><b>Details: </b>{studyAmendmentState.details}</p>}
                {studyAmendmentState.scope && <p><b>Scope: </b>{studyAmendmentState.scope}</p>}
                {studyAmendmentState.description && <p><b>Description: </b>{studyAmendmentState.description}</p>}
                {studyAmendmentState.rationale && <p><b>Rationale: </b>{studyAmendmentState.rationale}</p>}
                {studyAmendmentState.substantialImpactSafety === true && <p><b>Substantial Impact on Safety: </b>YES</p>}
                {studyAmendmentState.substantialImpactSafety === false && <p><b>Substantial Impact on Safety: </b>No</p>}
                {studyAmendmentState.substantialImpactSafetyComment && <p><b>Substantial Impact on Safety Comment: </b>{studyAmendmentState.substantialImpactSafetyComment}</p>}
                {studyAmendmentState.substantialImpactReliability === true && <p><b>Substantial Impact on Reliability: </b>YES</p>}
                {studyAmendmentState.substantialImpactReliability === false && <p><b>Substantial Impact on Reliability: </b>No</p>}
                {studyAmendmentState.substantialImpactReliabilityComment && <p><b>Substantial Impact on Reliability Comment: </b>{studyAmendmentState.substantialImpactReliabilityComment}</p>}
                {studyAmendmentState.primaryReason && <p><b>Primary Reason: </b>{displayStringFromFHIRCodeableConcept(studyAmendmentState.primaryReason)}</p>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(studyAmendmentState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='details' fieldLabel='Details'
                    startingValue={studyAmendmentState.details} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='scope' fieldLabel='Scope'
                    startingValue={studyAmendmentState.scope} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='description' fieldLabel='Description'
                    startingValue={studyAmendmentState.description} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='rationale' fieldLabel='Rationale'
                    startingValue={studyAmendmentState.rationale} setResourceState={setStudyAmendmentState} />
                <BooleanEntry elementName='substantialImpactSafety' fieldLabel='Substantial Impact on Safety'
                    storeFalse
                    startingValue={studyAmendmentState.substantialImpactSafety} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='substantialImpactSafetyComment' fieldLabel='Substantial Impact on Safety Comment'
                    startingValue={studyAmendmentState.substantialImpactSafetyComment} setResourceState={setStudyAmendmentState} />
                <BooleanEntry elementName='substantialImpactReliability' fieldLabel='Substantial Impact on Reliability'
                    storeFalse
                    startingValue={studyAmendmentState.substantialImpactReliability} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='substantialImpactReliabilityComment' fieldLabel='Substantial Impact on Reliability Comment'
                    startingValue={studyAmendmentState.substantialImpactReliabilityComment} setResourceState={setStudyAmendmentState} />
                <CodeableConceptEntry elementName='primaryReason' fieldLabel='Primary Reason'
                    startingValue={studyAmendmentState.primaryReason} setResourceState={setStudyAmendmentState} />
            </div>
        </>
    }
}

const EstimandEntry = ({ elementName, fieldLabel, startingValue, setResourceState }) => {

    const [estimandState, setEstimandState] = useState({});

    useEffect(() => {
        let startingEstimandEntry = {
            label: "", type: "", endpoint: "", population: "", interventionGroup: "", comparatorGroup: "", summaryMeasure: "", eventHandling: []
        }
        if (startingValue?.extension) {
            for (const ext of startingValue.extension) {
                if (ext.url === "label") {
                    startingEstimandEntry.label = ext.valueString;
                } else if (ext.url === "type") {
                    startingEstimandEntry.type = ext.valueCodeableConcept;
                } else if (ext.url === "endpoint") {
                    startingEstimandEntry.endpoint = ext.valueReference;
                } else if (ext.url === "population") {
                    startingEstimandEntry.population = ext.valueReference;
                } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                    startingEstimandEntry.interventionGroup = ext.valueId;
                } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                    startingEstimandEntry.comparatorGroup = ext.valueId;
                } else if (ext.url === "summaryMeasure") {
                    startingEstimandEntry.summaryMeasure = ext.valueCodeableConcept;
                } else if (ext.url === "eventHandling") {
                    startingEstimandEntry.eventHandling.push(ext);
                }
            }
        }
        setEstimandState(JSON.parse(JSON.stringify(startingEstimandEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(estimandState).length > 0) {
            let newEndpointExtension = {
                extension: [], url: "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand"
            };
            if (estimandState.label) {
                newEndpointExtension.extension.push({ "url": "label", "valueString": estimandState.label });
            }
            if (estimandState.type) {
                newEndpointExtension.extension.push({ "url": "type", "valueCodeableConcept": estimandState.type });
            }
            if (estimandState.endpoint) {
                newEndpointExtension.extension.push({ "url": "endpoint", "valueReference": estimandState.endpoint });
            }
            if (estimandState.population) {
                newEndpointExtension.extension.push({ "url": "population", "valueReference": estimandState.population });
            }
            if (estimandState.interventionGroup) {
                newEndpointExtension.extension.push({ "url": "interventionGroup", "valueId": estimandState.interventionGroup });
            }
            if (estimandState.comparatorGroup) {
                newEndpointExtension.extension.push({ "url": "comparatorGroup", "valueId": estimandState.comparatorGroup });
            }
            if (estimandState.summaryMeasure) {
                newEndpointExtension.extension.push({ "url": "summaryMeasure", "valueCodeableConcept": estimandState.summaryMeasure });
            }
            if (Array.isArray(estimandState.eventHandling) && estimandState.eventHandling.length > 0) {
                newEndpointExtension.extension = newEndpointExtension.extension.concat(estimandState.eventHandling);
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEndpointExtension }; });
        }
    }), [estimandState]);

    if (Object.keys(estimandState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='label' fieldLabel='Short name or identifier'
                    startingValue={estimandState.label} setResourceState={setEstimandState} />
                <CodeableConceptEntry elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet}
                    startingValue={estimandState.type} setResourceState={setEstimandState} />
                <ReferenceEntry elementName='endpoint' fieldLabel='Endpoint'
                    referencedResourceTypes={['EvidenceVariable']}
                    enableCreation={true} startCollapsed={true} startingResourceType="EvidenceVariable"
                    startingValue={estimandState.endpoint} setResourceState={setEstimandState} />
                <ReferenceEntry elementName='population' fieldLabel='Population'
                    referencedResourceTypes={['Group']} startingResourceType="Group"
                    enableCreation={true} startCollapsed={true}
                    startingValue={estimandState.population} setResourceState={setEstimandState} />
                <IdEntry elementName='interventionGroup' fieldLabel='Intervention Group'
                    startingValue={estimandState.interventionGroup} setResourceState={setEstimandState} />
                <IdEntry datatype='id' elementName='comparatorGroup' fieldLabel='Comparator Group'
                    startingValue={estimandState.comparatorGroup} setResourceState={setEstimandState} />
                <CodeableConceptEntry elementName='summaryMeasure' fieldLabel='Summary Measure'
                    valueSet={SEVCO.statisticType} codeableConceptLevelValueSet={statisticTypeCodeableConceptSet}
                    startingValue={estimandState.summaryMeasure} setResourceState={setEstimandState} />
                <ArrayEntry datatype='EventHandling' elementName='eventHandling' fieldLabel='Handling of Intercurrent Events'
                    startingValue={estimandState.eventHandling} setResourceState={setEstimandState} />
            </div>
        </>
    }
}

const researchStudyDotObjectiveTypeValueSet = [
    { system: "http://hl7.org/fhir/research-study-objective-type", code: "primary", display: "Primary" },
    { system: "http://hl7.org/fhir/research-study-objective-type", code: "secondary", display: "Secondary" },
    { system: "http://hl7.org/fhir/research-study-objective-type", code: "exploratory", display: "Exploratory" }
];
const ResearchStudyObjectiveEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [objectiveEntryState, setObjectiveEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
    const [objectiveEndpointsState, setObjectiveEndpointsState] = useState({ "references": [], "loaded": false });

    useEffect(() => {
        let startingObjectiveEntry = {
            name: "", type: "", description: "", outcomeMeasure: []
        };
        if (startingValue) {
            if (startingValue.extension) { startingObjectiveEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingObjectiveEntry.name = startingValue.name; }
            if (startingValue.type) { startingObjectiveEntry.type = startingValue.type; }
            if (startingValue.description) { startingObjectiveEntry.description = startingValue.description; }
            if (startingValue.outcomeMeasure) {
                startingObjectiveEntry.outcomeMeasure = startingValue.outcomeMeasure;
            }
        }
        if (!objectiveEndpointsState?.loaded) {
            let endpointReferences = [];
            if (startingObjectiveEntry.outcomeMeasure.length > 0) {
                for (const outcomeMeasure of startingObjectiveEntry.outcomeMeasure) {
                    if (outcomeMeasure.endpoint) {
                        endpointReferences.push(outcomeMeasure.endpoint);
                    } else {
                        endpointReferences.push({});
                    }
                }
            }
            setObjectiveEndpointsState({
                "references": endpointReferences, "loaded": true
            });
        }
        setObjectiveEntryState(JSON.parse(JSON.stringify(startingObjectiveEntry)));
    }, []);

    useEffect(() => {
        if (objectiveEndpointsState?.references?.length) {
            setObjectiveEntryState(prevState => {
                let newOutcomeMeasure = JSON.parse(JSON.stringify(prevState.outcomeMeasure)) || [];
                for (let index in objectiveEndpointsState.references) {
                    if (newOutcomeMeasure[index]) {
                        if (objectiveEndpointsState.references[index]) {
                            newOutcomeMeasure[index].endpoint = objectiveEndpointsState.references[index];
                        }
                    } else {
                        newOutcomeMeasure.push({
                            "endpoint": objectiveEndpointsState.references[index]
                        });
                    }
                }
                return { ...prevState, outcomeMeasure: newOutcomeMeasure }
            });
        }
    }, [objectiveEndpointsState]);

    useEffect((() => {
        if (Object.keys(objectiveEntryState).length > 0) {
            let newObjectiveEntry = {};
            if (objectiveEntryState.extension) { newObjectiveEntry.extension = objectiveEntryState.extension; }
            if (objectiveEntryState.name) { newObjectiveEntry.name = objectiveEntryState.name; }
            if (objectiveEntryState.type && Object.keys(objectiveEntryState.type).length > 0) {
                newObjectiveEntry.type = objectiveEntryState.type;
            }
            if (objectiveEntryState.description) { newObjectiveEntry.description = objectiveEntryState.description; }
            if (Array.isArray(objectiveEntryState.outcomeMeasure) && objectiveEntryState.outcomeMeasure.length) {
                newObjectiveEntry.outcomeMeasure = objectiveEntryState.outcomeMeasure;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newObjectiveEntry }; });
        }
    }), [objectiveEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.type} /><br /></span>}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {Array.isArray(startingValue.outcomeMeasure) &&
                    startingValue.outcomeMeasure.map((outcomeMeasure, outcomeMeasureIndex) => {
                        let name = outcomeMeasure.name || "";
                        let type = outcomeMeasure.type || "";
                        let description = outcomeMeasure.description || "";
                        let endpoint = outcomeMeasure.endpoint || "";
                        let population = outcomeMeasure.population || "";
                        let intervention = outcomeMeasure.intervention || "";
                        let comparator = outcomeMeasure.comparator || "";
                        let summaryMeasure = outcomeMeasure.summaryMeasure || "";
                        let eventHandling = outcomeMeasure.eventHandling || [];
                        return <div key={outcomeMeasureIndex} >
                            <h4>Outcome Measure {outcomeMeasureIndex + 1}</h4>
                            <div style={{ marginLeft: "24px" }}>
                                {name && <p><b>Name: </b>{name}</p>}
                                {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                {intervention && <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={intervention} /><br /></span>}
                                {comparator && <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={comparator} /><br /></span>}
                                {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                {Array.isArray(eventHandling) &&
                                    eventHandling.map((ice, iceIndex) => {
                                        let event = ice.event || "";
                                        let group = ice.group || "";
                                        let handling = ice.handling || "";
                                        let description = ice.description || "";
                                        return <div key={iceIndex}>
                                            <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                            <div style={{ marginLeft: "24px" }}>
                                                {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                            </div>
                                        </div>
                                    })}
                            </div>
                        </div>
                    })}
                {Array.isArray(startingValue.extension) &&
                    startingValue.extension.map((extension, extensionIndex) => {
                        if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand") {
                            let label;
                            let type
                            let endpoint;
                            let population;
                            let intervention;
                            let comparator;
                            let summaryMeasure;
                            let eventHandling = [];
                            for (const ext of extension.extension) {
                                if (ext.url === "label") {
                                    label = ext.valueString;
                                } else if (ext.url === "type") {
                                    type = ext.valueCodeableConcept;
                                } else if (ext.url === "endpoint") {
                                    endpoint = ext.valueReference;
                                } else if (ext.url === "population") {
                                    population = ext.valueReference;
                                } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                                    intervention = ext.valueId;
                                } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                                    comparator = ext.valueId;
                                } else if (ext.url === "summaryMeasure") {
                                    summaryMeasure = ext.valueCodeableConcept;
                                } else if (ext.url === "eventHandling") {
                                    eventHandling.push(ext);
                                }
                            }
                            return <div key={extensionIndex} >
                                <h4>Estimand {extensionIndex + 1}</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    {label && <p><b>Label: </b>{label}</p>}
                                    {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                    {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                    {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                    {intervention && <p><b>Intervention Group: </b>Comparison group {intervention}</p>}
                                    {comparator && <p><b>Comparator Group: </b>Comparison group {comparator}</p>}
                                    {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                    {Array.isArray(eventHandling) &&
                                        eventHandling.map((ice, iceIndex) => {
                                            let event;
                                            let group;
                                            let handling;
                                            let description;
                                            for (const extension of ice.extension) {
                                                if (extension.url === "event") {
                                                    event = extension.valueCodeableConcept;
                                                } else if (extension.url === "group") {
                                                    group = extension.valueCodeableConcept;
                                                } else if (extension.url === "handling") {
                                                    handling = extension.valueCodeableConcept;
                                                } else if (extension.url === "description") {
                                                    description = extension.valueMarkdown;
                                                }
                                            }
                                            return <div key={iceIndex}>
                                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </div>
                            </div>
                        } else {
                            return <></>;
                        }
                    })}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(objectiveEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={objectiveEntryState.name} setResourceState={setObjectiveEntryState} />
                <CodeableConceptEntry elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={startCollapsed}
                    startingValue={objectiveEntryState.type} setResourceState={setObjectiveEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed={startCollapsed}
                    startingValue={objectiveEntryState.description} setResourceState={setObjectiveEntryState} />
                    <br/>    <br/>
                {objectiveEndpointsState?.loaded && <>
                    <p><b>To manage the outcome measure entries as a simple set of resource references:</b></p>
                    <div style={{ marginLeft: "24px" }}>
                        <ArrayEntry datatype='Reference' elementName='references'
                            fieldLabel={'The VariableDefinition Resource'}
                            startingValue={objectiveEndpointsState.references || []}
                            setProfile={"VariableDefinition"}
                            startingResourceType={"EvidenceVariable"}
                            referencedResourceTypes={["EvidenceVariable"]}
                            startCollapsed startEmptyArrayClosed enableCreation={true}
                            setResourceState={setObjectiveEndpointsState} />
                    </div>
                </>}
                <ArrayEntry datatype="ResearchStudyObjectiveOutcomeMeasure"
                    elementName='outcomeMeasure' fieldLabel='Outcome Measure'
                    startingValue={objectiveEntryState.outcomeMeasure} setResourceState={setObjectiveEntryState}
                    startCollapsed={true} />
            </div>
        </>
    }
}

const ResearchStudyObjectiveOutcomeMeasureEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [outcomeMeasureEntryState, setOutcomeMeasureEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingOutcomeEntry = {
            name: "", type: "", description: "", endpoint: "",
            population: "", intervention: "", comparator: "", summaryMeasure: "", eventHandling: []
        };
        if (startingValue) {
            if (startingValue.extension) { startingOutcomeEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingOutcomeEntry.name = startingValue.name; }
            if (startingValue.type) { startingOutcomeEntry.type = startingValue.type; }
            if (startingValue.description) { startingOutcomeEntry.description = startingValue.description; }
            if (startingValue.endpoint) { startingOutcomeEntry.endpoint = startingValue.endpoint; }
            if (startingValue.population) { startingOutcomeEntry.population = startingValue.population; }
            if (startingValue.intervention) { startingOutcomeEntry.intervention = startingValue.intervention; }
            if (startingValue.comparator) { startingOutcomeEntry.comparator = startingValue.comparator; }
            if (startingValue.summaryMeasure) { startingOutcomeEntry.summaryMeasure = startingValue.summaryMeasure; }
            if (startingValue.eventHandling) { startingOutcomeEntry.eventHandling = startingValue.eventHandling; }
        }
        setOutcomeMeasureEntryState(JSON.parse(JSON.stringify(startingOutcomeEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(outcomeMeasureEntryState).length > 0) {
            let newOutcomeEntry = {};
            if (outcomeMeasureEntryState.extension) { newOutcomeEntry.extension = outcomeMeasureEntryState.extension; }
            if (outcomeMeasureEntryState.name) { newOutcomeEntry.name = outcomeMeasureEntryState.name; }
            if (outcomeMeasureEntryState.type && Object.keys(outcomeMeasureEntryState.type).length > 0) {
                newOutcomeEntry.type = outcomeMeasureEntryState.type;
            }
            if (outcomeMeasureEntryState.description) { newOutcomeEntry.description = outcomeMeasureEntryState.description; }
            if (outcomeMeasureEntryState.endpoint && Object.keys(outcomeMeasureEntryState.endpoint).length > 0) {
                newOutcomeEntry.endpoint = outcomeMeasureEntryState.endpoint;
            }
            if (outcomeMeasureEntryState.population && Object.keys(outcomeMeasureEntryState.population).length > 0) {
                newOutcomeEntry.population = outcomeMeasureEntryState.population;
            }
            if (outcomeMeasureEntryState.intervention && Object.keys(outcomeMeasureEntryState.intervention).length > 0) {
                newOutcomeEntry.intervention = outcomeMeasureEntryState.intervention;
            }
            if (outcomeMeasureEntryState.comparator && Object.keys(outcomeMeasureEntryState.comparator).length > 0) {
                newOutcomeEntry.comparator = outcomeMeasureEntryState.comparator;
            }
            if (outcomeMeasureEntryState.summaryMeasure && Object.keys(outcomeMeasureEntryState.summaryMeasure).length > 0) {
                newOutcomeEntry.summaryMeasure = outcomeMeasureEntryState.summaryMeasure;
            }
            if (Array.isArray(outcomeMeasureEntryState.eventHandling) && outcomeMeasureEntryState.eventHandling.length > 0) {
                newOutcomeEntry.eventHandling = outcomeMeasureEntryState.eventHandling;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newOutcomeEntry }; });
        }
    }), [outcomeMeasureEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.type} /><br /></span>}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={startingValue.endpoint} /><br /></span>}
                {startingValue.population && <span><b>Population: </b><DisplayFromFHIRReference reference={startingValue.population} /><br /></span>}
                {startingValue.intervention && <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={startingValue.intervention} /><br /></span>}
                {startingValue.comparator && <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={startingValue.comparator} /><br /></span>}
                {startingValue.summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.summaryMeasure} /><br /></span>}
                {Array.isArray(startingValue.eventHandling) &&
                    startingValue.eventHandling.map((ice, iceIndex) => {
                        let event = ice.event || "";
                        let group = ice.group || "";
                        let handling = ice.handling || "";
                        let description = ice.description || "";
                        return <div key={iceIndex}>
                            <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                            <div style={{ marginLeft: "24px" }}>
                                {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                            </div>
                        </div>
                    })}
                {Array.isArray(startingValue.extension) &&
                    startingValue.extension.map((extension, extensionIndex) => {
                        if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand") {
                            let label;
                            let type
                            let endpoint;
                            let population;
                            let intervention;
                            let comparator;
                            let summaryMeasure;
                            let eventHandling = [];
                            for (const ext of extension.extension) {
                                if (ext.url === "label") {
                                    label = ext.valueString;
                                } else if (ext.url === "type") {
                                    type = ext.valueCodeableConcept;
                                } else if (ext.url === "endpoint") {
                                    endpoint = ext.valueReference;
                                } else if (ext.url === "population") {
                                    population = ext.valueReference;
                                } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                                    intervention = ext.valueId;
                                } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                                    comparator = ext.valueId;
                                } else if (ext.url === "summaryMeasure") {
                                    summaryMeasure = ext.valueCodeableConcept;
                                } else if (ext.url === "eventHandling") {
                                    eventHandling.push(ext);
                                }
                            }
                            return <div key={extensionIndex} >
                                <h4>Estimand {extensionIndex + 1}</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    {label && <p><b>Label: </b>{label}</p>}
                                    {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                    {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                    {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                    {intervention && <p><b>Intervention Group: </b>Comparison group {intervention}</p>}
                                    {comparator && <p><b>Comparator Group: </b>Comparison group {comparator}</p>}
                                    {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                    {Array.isArray(eventHandling) &&
                                        eventHandling.map((ice, iceIndex) => {
                                            let event;
                                            let group;
                                            let handling;
                                            let description;
                                            for (const extension of ice.extension) {
                                                if (extension.url === "event") {
                                                    event = extension.valueCodeableConcept;
                                                } else if (extension.url === "group") {
                                                    group = extension.valueCodeableConcept;
                                                } else if (extension.url === "handling") {
                                                    handling = extension.valueCodeableConcept;
                                                } else if (extension.url === "description") {
                                                    description = extension.valueMarkdown;
                                                }
                                            }
                                            return <div key={iceIndex}>
                                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </div>
                            </div>
                        } else {
                            return <></>;
                        }
                    })}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(outcomeMeasureEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={outcomeMeasureEntryState.name} setResourceState={setOutcomeMeasureEntryState} />
                <CodeableConceptEntry elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.type} setResourceState={setOutcomeMeasureEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.description} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='endpoint' fieldLabel='Endpoint'
                    referencedResourceTypes={['EvidenceVariable']}
                    startingResourceType={"EvidenceVariable"}
                    enableCreation={true}
                    startingValue={outcomeMeasureEntryState.endpoint} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='population' fieldLabel='Population'
                    referencedResourceTypes={['Group']}
                    startingResourceType={"Group"}
                    enableCreation={true}
                    startingValue={outcomeMeasureEntryState.population} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='intervention' fieldLabel='Intervention Group'
                    referencedResourceTypes={['Group']}
                    startingValue={outcomeMeasureEntryState.population} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='comparator' fieldLabel='Comparator Group'
                    referencedResourceTypes={['Group']}
                    startingValue={outcomeMeasureEntryState.population} setResourceState={setOutcomeMeasureEntryState} />
                <CodeableConceptEntry elementName='summaryMeasure' fieldLabel='Summary Measure'
                    valueSet={SEVCO.statisticType} codeableConceptLevelValueSet={statisticTypeCodeableConceptSet}
                    startingValue={outcomeMeasureEntryState.summaryMeasure} setResourceState={setOutcomeMeasureEntryState} />
                <ArrayEntry datatype='EventHandling' elementName='eventHandling' fieldLabel='Handling of Intercurrent Events'
                    startingValue={outcomeMeasureEntryState.eventHandling} setResourceState={setOutcomeMeasureEntryState} />
            </div>
        </>
    }
}

const ResearchStudyOutcomeMeasureEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [outcomeMeasureEntryState, setOutcomeMeasureEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingOutcomeEntry = {
            name: "", type: "", description: "", reference: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingOutcomeEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingOutcomeEntry.name = startingValue.name; }
            if (startingValue.type) { startingOutcomeEntry.type = startingValue.type; }
            if (startingValue.description) { startingOutcomeEntry.description = startingValue.description; }
            if (startingValue.reference) { startingOutcomeEntry.reference = startingValue.reference; }
        }
        setOutcomeMeasureEntryState(JSON.parse(JSON.stringify(startingOutcomeEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(outcomeMeasureEntryState).length > 0) {
            let newOutcomeEntry = {};
            if (outcomeMeasureEntryState.extension) { newOutcomeEntry.extension = outcomeMeasureEntryState.extension; }
            if (outcomeMeasureEntryState.name) { newOutcomeEntry.name = outcomeMeasureEntryState.name; }
            if (outcomeMeasureEntryState.type && Object.keys(outcomeMeasureEntryState.type).length > 0) {
                newOutcomeEntry.type = outcomeMeasureEntryState.type;
            }
            if (outcomeMeasureEntryState.description) { newOutcomeEntry.description = outcomeMeasureEntryState.description; }
            if (outcomeMeasureEntryState.reference && Object.keys(outcomeMeasureEntryState.reference).length > 0) {
                newOutcomeEntry.reference = outcomeMeasureEntryState.reference;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newOutcomeEntry }; });
        }
    }), [outcomeMeasureEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {Array.isArray(startingValue.type) &&
                    startingValue.type.map((type, typeIndex) => {
                        return <div key={typeIndex}><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /></div>
                    })}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.reference && <span><b>Outcome Definition: </b><DisplayFromFHIRReference reference={startingValue.reference} /><br /></span>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(outcomeMeasureEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={outcomeMeasureEntryState.name} setResourceState={setOutcomeMeasureEntryState} />
                <ArrayEntry datatype='CodeableConcept' elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.type} setResourceState={setOutcomeMeasureEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.description} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='reference' fieldLabel='Outcome Definition'
                    referencedResourceTypes={['EvidenceVariable']} startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.reference} setResourceState={setOutcomeMeasureEntryState} />
            </div>
        </>
    }
}

const researchStudyDotProgressStatusValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "overall-study", display: "Overall study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "active", display: "Active" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "active-but-not-recruiting", display: "Active, not recruiting" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "administratively-completed", display: "Administratively Completed" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "approved", display: "Approved" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "closed-to-accrual", display: "Closed to Accrual" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "closed-to-accrual-and-intervention", display: "Closed to Accrual and Intervention" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "completed", display: "Completed" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "disapproved", display: "Disapproved" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "enrolling-by-invitation", display: "Enrolling by invitation" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "in-review", display: "In Review" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "not-yet-recruiting", display: "Not yet recruiting" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "recruiting", display: "Recruiting" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "temporarily-closed-to-accrual", display: "Temporarily Closed to Accrual" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "temporarily-closed-to-accrual-and-intervention", display: "Temporarily Closed to Accrual and Intervention" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "terminated", display: "Terminated" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "withdrawn", display: "Withdrawn" }
];
const ResearchStudyProgressStatusEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [progressStatusEntryState, setProgressStatusEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingProgressStatusEntry = {
            state: "", actual: "", period: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingProgressStatusEntry.extension = startingValue.extension; }
            if (startingValue.state) { startingProgressStatusEntry.state = startingValue.state; }
            if (typeof startingValue.actual === "boolean") { startingProgressStatusEntry.actual = startingValue.actual; }
            if (startingValue.period) { startingProgressStatusEntry.period = startingValue.period; }
        }
        setProgressStatusEntryState(JSON.parse(JSON.stringify(startingProgressStatusEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(progressStatusEntryState).length > 0) {
            let newProgressEntry = {};
            if (progressStatusEntryState.extension) { newProgressEntry.extension = progressStatusEntryState.extension; }
            if (progressStatusEntryState.state && Object.keys(progressStatusEntryState.state).length > 0) {
                newProgressEntry.state = progressStatusEntryState.state;
            }
            if (typeof progressStatusEntryState.actual === "boolean") {
                newProgressEntry.actual = progressStatusEntryState.actual;
            }
            if (progressStatusEntryState.period && Object.keys(progressStatusEntryState.period).length > 0) {
                newProgressEntry.period = progressStatusEntryState.period;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newProgressEntry }; });
        }
    }), [progressStatusEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div>
                <div style={{ marginLeft: "24px" }}>
                    {startingValue.state && <span><b>State: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.state} /><br /></span>}
                    {startingValue.actual === true && <p><b>Actual</b></p>}
                    {startingValue.actual === false && <p><b>Anticipated</b></p>}
                    {startingValue.period && <p><b>Period: </b>{displayStringFromFHIRPeriod(startingValue.period)}</p>}
                </div>
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(progressStatusEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='state' fieldLabel='Progress state'
                    valueSet={researchStudyDotProgressStatusValueSet}
                    startingValue={progressStatusEntryState.state} setResourceState={setProgressStatusEntryState} />
                <BooleanEntry elementName='actual' fieldLabel='Actual (false if Anticipated)'
                    storeFalse={true}
                    startingValue={progressStatusEntryState.actual} setResourceState={setProgressStatusEntryState} />
                <PeriodEntry elementName='period' fieldLabel='Period (date range)'
                    startingValue={progressStatusEntryState.period} setResourceState={setProgressStatusEntryState} />
            </div>
        </>
    }
}

const ResearchStudyRecruitmentEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [recruitmentEntryState, setRecruitmentEntryState] = useState({});

    useEffect(() => {
        let startingRecruitmentEntry = {
            targetNumber: "", actualNumber: "", eligibility: "", actualGroup: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingRecruitmentEntry.extension = startingValue.extension; }
            if (startingValue.targetNumber || startingValue.targetNumber === 0) { startingRecruitmentEntry.targetNumber = startingValue.targetNumber; }
            if (startingValue.actualNumber || startingValue.actualNumber === 0) { startingRecruitmentEntry.actualNumber = startingValue.actualNumber; }
            if (startingValue.eligibility) { startingRecruitmentEntry.eligibility = startingValue.eligibility; }
            if (startingValue.actualGroup) { startingRecruitmentEntry.actualGroup = startingValue.actualGroup; }
        }
        setRecruitmentEntryState(JSON.parse(JSON.stringify(startingRecruitmentEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(recruitmentEntryState).length > 0) {
            let newRecruitmentEntry = {};
            if (recruitmentEntryState.extension) { newRecruitmentEntry.extension = recruitmentEntryState.extension; }
            if (recruitmentEntryState.targetNumber || recruitmentEntryState.targetNumber === 0) { newRecruitmentEntry.targetNumber = recruitmentEntryState.targetNumber; }
            if (recruitmentEntryState.actualNumber || recruitmentEntryState.actualNumber === 0) { newRecruitmentEntry.actualNumber = recruitmentEntryState.actualNumber; }
            if (recruitmentEntryState.eligibility && Object.keys(recruitmentEntryState.eligibility).length > 0) {
                newRecruitmentEntry.eligibility = recruitmentEntryState.eligibility;
            }
            if (recruitmentEntryState.actualGroup && Object.keys(recruitmentEntryState.actualGroup).length > 0) {
                newRecruitmentEntry.actualGroup = recruitmentEntryState.actualGroup;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newRecruitmentEntry }; });
        }
    }), [recruitmentEntryState]);

    if (Object.keys(recruitmentEntryState).length > 0) {
        return <>
            <div style={{ marginLeft: "2px" }}>
                <UnsignedIntEntry elementName='targetNumber' fieldLabel='Target number of participants'
                    startingValue={recruitmentEntryState.targetNumber} setResourceState={setRecruitmentEntryState} />
                <UnsignedIntEntry elementName='actualNumber' fieldLabel='Actual number of participants'
                    startingValue={recruitmentEntryState.actualNumber} setResourceState={setRecruitmentEntryState} />
                <ReferenceEntry
                    elementName='eligibility' fieldLabel='Eligibility Criteria' startCollapsed={startCollapsed}
                    referencedResourceTypes={['Group']} enableCreation={true}
                    startingValue={recruitmentEntryState.eligibility} setResourceState={setRecruitmentEntryState} />
                <ReferenceEntry
                    elementName='actualGroup' fieldLabel='Enrollment Group'
                    referencedResourceTypes={['Group']} enableCreation={true} startCollapsed={startCollapsed}
                    startingValue={recruitmentEntryState.actualGroup} setResourceState={setRecruitmentEntryState} />
            </div>
        </>
    }
}

const researchStudyRegistrationActivityValueSet = [
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "record-verification", display: "Record Verification" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "primary-outcome-data-collection", display: "Primary outcome data collection" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "registration-submission", display: "Registration submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "registration-submission-qc", display: "Registration submission Quality Check" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "registration-posting", display: "Registration posting" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "results-submission", display: "Results submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "results-submission-qc", display: "Results submission Quality Check" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "results-posting", display: "Results posting" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "disposition-submission", display: "Disposition submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "disposition-submission-qc", display: "Disposition submission Quality Check" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "disposition-posting", display: "Disposition posting" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "update-submission", display: "Update submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "update-posting", display: "Update posting" }
];
const ResearchStudyRegistrationExtensionEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [simpleExtensionValuesState, setSimpleExtensionValuesState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingExtensionEntryValues = {
            "activity": "", "actual": null, "period": ""
        };
        if (startingValue?.extension) {
            for (const ext of startingValue.extension) {
                if (ext.url === "activity" && ext.valueCodeableConcept) {
                    startingExtensionEntryValues.activity = ext.valueCodeableConcept;
                } else if (ext.url === "actual" && typeof ext.valueBoolean === "boolean") {
                    startingExtensionEntryValues.actual = ext.valueBoolean;
                } else if (ext.url === "period" && ext.valuePeriod) {
                    startingExtensionEntryValues.period = ext.valuePeriod;
                }
            }
        }
        setSimpleExtensionValuesState(startingExtensionEntryValues);
    }, []);

    useEffect((() => {
        if (Object.keys(simpleExtensionValuesState).length > 0) {
            let newRegistrationExtensionEntry = {
                extension: [],
                url: "http://hl7.org/fhir/StructureDefinition/researchStudy-studyRegistration"
            };
            if (simpleExtensionValuesState.activity) {
                newRegistrationExtensionEntry.extension.push({
                    url: "activity", valueCodeableConcept: simpleExtensionValuesState.activity
                });
            }
            if (typeof simpleExtensionValuesState.actual === "boolean") {
                newRegistrationExtensionEntry.extension.push({
                    url: "actual", valueBoolean: simpleExtensionValuesState.actual
                });
            }
            if (simpleExtensionValuesState.period) {
                newRegistrationExtensionEntry.extension.push({
                    url: "period", valuePeriod: simpleExtensionValuesState.period
                });
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newRegistrationExtensionEntry }; });
        }
    }), [simpleExtensionValuesState]);

    if (startCollapsedState && simpleExtensionValuesState.activity) {
        return <div>
            <b>{fieldLabel}: </b>
            <div style={{ marginLeft: "24px" }}>
                {simpleExtensionValuesState.activity && <span>
                    <b>State: </b>
                    <DisplayFromFHIRCodeableConcept codeableConcept={simpleExtensionValuesState.activity} />
                    <br />
                </span>}
                {simpleExtensionValuesState.actual === true && <p><b>Actual</b></p>}
                {simpleExtensionValuesState.actual === false && <p><b>Anticipated</b></p>}
                {simpleExtensionValuesState.period &&
                    <p><b>Period: </b>{displayStringFromFHIRPeriod(simpleExtensionValuesState.period)}</p>}
            </div>
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='activity' fieldLabel='Study registration state'
                    valueSet={researchStudyRegistrationActivityValueSet}
                    startingValue={simpleExtensionValuesState.activity} setResourceState={setSimpleExtensionValuesState} />
                <BooleanEntry elementName='actual' fieldLabel='Actual (false if Anticipated)'
                    storeFalse={true}
                    startingValue={simpleExtensionValuesState.actual} setResourceState={setSimpleExtensionValuesState} />
                <PeriodEntry elementName='period' fieldLabel='Period (date range)'
                    startingValue={simpleExtensionValuesState.period} setResourceState={setSimpleExtensionValuesState} />
            </div>
        </>
    }
}

const ResearchStudyDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    let isSystematicReviewProfile;
    let isM11ResearchStudyProfile;
    let isStudyDesignProfile;
    let isStudyRegistryRecordProfile;
    if (resourceJson.meta?.profile?.length) {
        for (const profile of resourceJson.meta.profile) {
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/systematic-review") {
                isSystematicReviewProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/pharmaceutical-research-protocol/StructureDefinition/m11-research-study-profile") {
                isM11ResearchStudyProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-design") {
                isStudyDesignProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-registry-record" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-research-study") {
                isStudyRegistryRecordProfile = true;
            }
        }
    }

    let nctid;
    let ctisid;
    if (resourceJson.identifier) {
        for (let identifierIndex in resourceJson.identifier) {
            let identifier = resourceJson.identifier[identifierIndex];
            if (identifier.system === "https://clinicaltrials.gov" && identifier.value) {
                nctid = identifier.value;
            }
            if (identifier.system === "https://euclinicaltrials.eu" && identifier.value) {
                ctisid = identifier.value;
            }
        }
    }

    const [researchStudyResourceState, setResearchStudyResourceState] = useState({});
    const [extensionState, setExtensionState] = useState({});

    useEffect(() => {
        dispatch(changeTool("FEvIR®: ResearchStudy " + (editMode ? "Builder" : "Viewer")));
        dispatch(changeBrowserTabTitle("FEvIR ResearchStudy - " + displayTitle));
    }, [displayTitle, editMode]);

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Summary", "Identification", "Status", "Subject", "Protocol",
            "Organizations", "Recruitment", "Variables", "Results"
        ]));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setResearchStudyResourceState(olderVersionJson);
                let startingExtension = olderVersionJson.extension || [];
                let startingExtensionStateValue = {
                    "otherExtensions": [], "searchStrategy": [], "studyRegistration": [],
                    "includedStudy": [], "excludedStudy": []
                }
                if (Array.isArray(startingExtension) && startingExtension.length > 0) {
                    for (const extension of startingExtension) {
                        switch (extension.url) {
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-search-strategy':
                                startingExtensionStateValue.searchStrategy.push(extension.valueReference);
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-systematic-review-eligibility-criteria':
                                startingExtensionStateValue.systematicReviewEligibilityCriteria = extension.valueReference;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-identified':
                                startingExtensionStateValue.numberOfStudiesIdentified = extension.valueUnsignedInt;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-included':
                                startingExtensionStateValue.numberOfStudiesIncluded = extension.valueUnsignedInt;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-included-study':
                                startingExtensionStateValue.includedStudy.push(extension.valueReference);
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-excluded-study':
                                startingExtensionStateValue.excludedStudy.push(extension.valueReference);
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement':
                                startingExtensionStateValue.confidentialityStatement = extension.valueString;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sae-reporting-method':
                                startingExtensionStateValue.saeReportingMethod = extension.valueString;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-study-amendment':
                                startingExtensionStateValue.studyAmendment = extension;
                                break;
                            case 'http://hl7.org/fhir/StructureDefinition/researchStudy-studyRegistration':
                                startingExtensionStateValue.studyRegistration.push(extension);
                                break;
                            default:
                                startingExtensionStateValue.otherExtensions.push(extension)
                        }
                    }
                }
                setExtensionState(startingExtensionStateValue);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setResearchStudyResourceState(resourceJson);
            let startingExtension = resourceJson.extension || [];
            let startingExtensionStateValue = {
                "otherExtensions": [], "searchStrategy": [], "studyRegistration": [],
                "includedStudy": [], "excludedStudy": []
            }
            if (Array.isArray(startingExtension) && startingExtension.length > 0) {
                for (const extension of startingExtension) {
                    switch (extension.url) {
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-search-strategy':
                            startingExtensionStateValue.searchStrategy.push(extension.valueReference);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-systematic-review-eligibility-criteria':
                            startingExtensionStateValue.systematicReviewEligibilityCriteria = extension.valueReference;
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-identified':
                            startingExtensionStateValue.numberOfStudiesIdentified = extension.valueUnsignedInt;
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-included':
                            startingExtensionStateValue.numberOfStudiesIncluded = extension.valueUnsignedInt;
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-included-study':
                            startingExtensionStateValue.includedStudy.push(extension.valueReference);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-excluded-study':
                            startingExtensionStateValue.excludedStudy.push(extension.valueReference);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement':
                            startingExtensionStateValue.confidentialityStatement = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sae-reporting-method':
                            startingExtensionStateValue.saeReportingMethod = extension.valueString;
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-study-amendment':
                            startingExtensionStateValue.studyAmendment = extension;
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/researchStudy-studyRegistration':
                            startingExtensionStateValue.studyRegistration.push(extension);
                            break;
                        default:
                            startingExtensionStateValue.otherExtensions.push(extension)
                    }
                }
            }
            setExtensionState(startingExtensionStateValue);
        }
    }, [resourceJson]);

    useEffect(() => {
        let newExtension = [];
        if (extensionState.otherExtensions) {
            newExtension = JSON.parse(JSON.stringify(extensionState.otherExtensions));
        }
        if (extensionState.searchStrategy?.length > 0) {
            for (const instance of extensionState.searchStrategy) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-search-strategy",
                    "valueReference": instance
                });
            }
        }
        if (extensionState.systematicReviewEligibilityCriteria) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-systematic-review-eligibility-criteria",
                "valueReference": extensionState.systematicReviewEligibilityCriteria
            });
        }
        if (!isNaN(extensionState.numberOfStudiesIdentified)) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-identified",
                "valueUnsignedInt": extensionState.numberOfStudiesIdentified
            });
        }
        if (!isNaN(extensionState.numberOfStudiesIncluded) || extensionState.numberOfStudiesIncluded === "") {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-included",
                "valueUnsignedInt": extensionState.numberOfStudiesIncluded
            });
        }
        if (extensionState.includedStudy?.length > 0) {
            for (const instance of extensionState.includedStudy) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-included-study",
                    "valueReference": instance
                });
            }
        }
        if (extensionState.excludedStudy?.length > 0) {
            for (const instance of extensionState.excludedStudy) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-excluded-study",
                    "valueReference": instance
                });
            }
        }
        if (extensionState.confidentialityStatement || extensionState.confidentialityStatement === "") {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement",
                "valueString": extensionState.confidentialityStatement
            });
        }
        if (extensionState.saeReportingMethod || extensionState.saeReportingMethod === "") {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sae-reporting-method",
                "valueString": extensionState.saeReportingMethod
            });
        }
        if (extensionState.studyAmendment && Object.keys(extensionState.studyAmendment).length > 0) {
            newExtension.push(extensionState.studyAmendment);
        }
        if (extensionState.studyRegistration?.length > 0) {
            for (const instance of extensionState.studyRegistration) {
                newExtension.push(instance);
            }
        }
        setResearchStudyResourceState(prevState => { return { ...prevState, "extension": newExtension } });
    }, [extensionState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(researchStudyResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = researchStudyResourceState.title;
            newResourceDetail.displayTitle = researchStudyResourceState.title ||
                researchStudyResourceState.name || newResourceDetail.displayTitle;
            if (researchStudyResourceState.title !== undefined &&
                previousFinalValue !== researchStudyResourceState.title) {
                console.log("resourceUpdate for ResearchStudy.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    researchStudyResourceState.title);
            }
        }
    }, [researchStudyResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = researchStudyResourceState.name;
            newResourceDetail.displayTitle = researchStudyResourceState.title ||
                researchStudyResourceState.name || newResourceDetail.displayTitle;
            if (researchStudyResourceState.name !== undefined &&
                previousFinalValue !== researchStudyResourceState.name) {
                console.log("resourceUpdate for ResearchStudy.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "name",
                    previousInitialValue,
                    researchStudyResourceState.name);
            }
        }
    }, [researchStudyResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "extension", "ArrayOfExtension", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.extension]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "label", "ArrayOfResearchStudyLabel", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.label]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "identifier", "ArrayOfIdentifier", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.identifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "version", "string", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "partOf", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.partOf]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "period", "Period", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.period]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "progressStatus", "ArrayOfResearchStudyProgressStatus", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.progressStatus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "whyStopped", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.whyStopped]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "descriptionSummary", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.descriptionSummary]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "primaryPurposeType", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.primaryPurposeType]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "focus", "ArrayOfCodeableReference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.focus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "condition", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.condition]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "keyword", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.keyword]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "classifier", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.classifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "region", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.region]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "phase", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.phase]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "studyDesign", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.studyDesign]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "protocol", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.protocol]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "relatesTo", "ArrayOfRelatesTo", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.relatesTo]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.note]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "associatedParty", "ArrayOfResearchStudyAssociatedParty", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.associatedParty]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "site", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.site]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "recruitment", "ResearchStudyRecruitment", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.recruitment]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "comparisonGroup", "ArrayOfResearchStudyComparisonGroup", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.comparisonGroup]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "objective", "ArrayOfResearchStudyObjective", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.objective]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "outcomeMeasure", "ArrayOfResearchStudyOutcomeMeasure", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.outcomeMeasure]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "result", "ArrayOfReference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [researchStudyResourceState.result]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {researchStudyResourceState.id && <>
            <div>
                {nctid && <div style={{ width: "100%", textAlign: "center" }}>
                    <b>VIEW on ClinicalTrials.gov: <a href={"https://clinicaltrials.gov/study/" + nctid}
                        target="_blank" rel="noopener noreferrer">{nctid}</a></b>
                </div>}
                {(nctid && ctisid) && <><br /></>}
                {ctisid && <div style={{ width: "100%", textAlign: "center" }}>
                    <b>VIEW on EU CTIS: <a href={"https://euclinicaltrials.eu/search-for-clinical-trials/?lang=en&EUCT=" + ctisid}
                        target="_blank" rel="noopener noreferrer">{ctisid}</a></b>
                </div>}
                <div style={{ marginTop: "12px" }}>
                    <h3 id="summary">Summary</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                        generatedNarrative={generateNarrativeForResearchStudyResource(researchStudyResourceState)} startCollapsed
                                        startingValue={researchStudyResourceState.text} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {(researchStudyResourceState.text?.div && researchStudyResourceState.text.status && researchStudyResourceState.text.status !== "empty") ?
                                        <DisplayFromFHIRXhtml xhtml={researchStudyResourceState.text.div} />
                                        : <p>[Narrative summary not generated.]</p>}
                                </>
                        }
                    </div>
                    <h3 id="identification">Identification</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <StringEntry elementName='title' fieldLabel='Title'
                                        startingValue={researchStudyResourceState.title} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.title && <p><b>Title: </b>{researchStudyResourceState.title}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <StringEntry elementName='name' fieldLabel='Name'
                                        startingValue={researchStudyResourceState.name} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.name && <p><b>Name: </b>{researchStudyResourceState.name}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyLabel' elementName='label' fieldLabel='Label'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.label} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.label) &&
                                        researchStudyResourceState.label.map((label, labelIndex) => {
                                            let labelType = "Label";
                                            if (label.type) {
                                                labelType = simpleDisplayStringFromFHIRCodeableConcept(label.type);
                                            }
                                            if (label.language) {
                                                return <p key={labelIndex}>
                                                    <b>{labelType}, Language -- {languageCodeDisplay[label.language]}: </b>{label.value}
                                                </p>
                                            } else {
                                                return <p key={labelIndex}><b>{labelType}: </b>{label.value}</p>
                                            }
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Identifier' elementName='identifier' fieldLabel='Identifier'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.identifier} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.identifier) &&
                                        researchStudyResourceState.identifier.map((identifier, identifierIndex) => {
                                            return <div key={identifierIndex}><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <StringEntry elementName='version' fieldLabel='Version'
                                        startingValue={researchStudyResourceState.version} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.version && <p><b>Version: </b>{researchStudyResourceState.version}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Reference' elementName='partOf'
                                        fieldLabel='Part of (ResearchStudy)'
                                        referencedResourceTypes={['ResearchStudy']} enableCreation={true} startCollapsed
                                        startingValue={researchStudyResourceState.partOf || null} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.partOf) &&
                                        researchStudyResourceState.partOf.map((partOf, partOfIndex) => {
                                            return <div key={partOfIndex}><b>Part of: </b><DisplayFromFHIRReference reference={partOf} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="status">Status</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {(isM11ResearchStudyProfile || isStudyDesignProfile) && <StringEntry
                                        elementName='confidentialityStatement'
                                        fieldLabel='Seponsor Confidentiality Statement'
                                        startingValue={extensionState.confidentialityStatement}
                                        setResourceState={setExtensionState} />}
                                </>
                                :
                                <>
                                    {extensionState.confidentialityStatement &&
                                        <p><b>Sponsor Confidentiality Statement: </b>{extensionState.confidentialityStatement}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <PeriodEntry elementName='period' fieldLabel='Study start and end'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.period} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.period &&
                                        <p><b>Study start and end: </b>{displayStringFromFHIRPeriod(researchStudyResourceState.period)}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyProgressStatus'
                                        elementName='progressStatus' fieldLabel='Progress Status' startCollapsed
                                        startingValue={researchStudyResourceState.progressStatus} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.progressStatus) &&
                                        researchStudyResourceState.progressStatus.map((progress, progressIndex) => {
                                            return <div key={progressIndex}><b>Progress Status: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {progress.state && <span><b>State: </b><DisplayFromFHIRCodeableConcept codeableConcept={progress.state} /><br /></span>}
                                                    {progress.actual === true && <p><b>Actual</b></p>}
                                                    {progress.actual === false && <p><b>Anticipated</b></p>}
                                                    {progress.period && <p><b>Period: </b>{displayStringFromFHIRPeriod(progress.period)}</p>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <CodeableConceptEntry elementName='whyStopped' fieldLabel='Why stopped'
                                        valueSet={researchStudyDotWhyStoppedValueSet} startCollapsed
                                        startingValue={researchStudyResourceState.whyStopped} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.whyStopped &&
                                        <p><b>Why stopped: </b><DisplayFromFHIRCodeableConcept codeableConcept={researchStudyResourceState.whyStopped} /></p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyRegistrationExtension'
                                        elementName='studyRegistration' fieldLabel='Study Registration Status' startCollapsed
                                        startEmptyArrayClosed={true}
                                        startingValue={extensionState.studyRegistration} setResourceState={setExtensionState} />
                                </>
                                :
                                <>
                                    {extensionState.studyRegistration?.length > 0 &&
                                        extensionState.studyRegistration.map((registrationStatus, registrationStatusIndex) => {
                                            let activity;
                                            let actual;
                                            let period;
                                            if (registrationStatus.extension) {
                                                for (const extension of registrationStatus.extension) {
                                                    if (extension.url === "activity" && extension.valueCodeableConcept) {
                                                        activity = extension.valueCodeableConcept;
                                                    } else if (extension.url === "period" && extension.valuePeriod) {
                                                        period = extension.valuePeriod;
                                                    } else if (extension.url === "actual" && typeof extension.valueBoolean === "boolean") {
                                                        actual = extension.valueBoolean;
                                                    }
                                                }
                                            }
                                            return <div key={registrationStatusIndex}>
                                                <b>Study Registration Status:</b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {activity && <span><b>State: </b><DisplayFromFHIRCodeableConcept codeableConcept={activity} /><br /></span>}
                                                    {actual === true && <p><b>Actual</b></p>}
                                                    {actual === false && <p><b>Anticipated</b></p>}
                                                    {period && <p><b>Period: </b>{displayStringFromFHIRPeriod(period)}</p>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="subject">Subject</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <MarkdownEntry elementName='descriptionSummary' fieldLabel='Brief Description'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.descriptionSummary} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.descriptionSummary &&
                                        <div>
                                            <b>Brief Description: </b><DisplayFromFHIRMarkdown markdown={researchStudyResourceState.descriptionSummary} />
                                            <br /><br />
                                        </div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <MarkdownEntry elementName='description' fieldLabel='Full Description'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.description} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.description &&
                                        <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={researchStudyResourceState.description} /><br /><br /></div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <CodeableConceptEntry elementName='primaryPurposeType' fieldLabel='Purpose Type'
                                        valueSet={researchStudyDotPrimaryPurposeTypeValueSet} startCollapsed
                                        startingValue={researchStudyResourceState.primaryPurposeType} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.primaryPurposeType &&
                                        <span><b>Purpose Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={researchStudyResourceState.primaryPurposeType} /><br /></span>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableReference'
                                        elementName='focus' fieldLabel='Focus' startCollapsed deletable deletableArray
                                        referencedResourceTypes={focusReferenceResourceTypes}
                                        startingValue={researchStudyResourceState.focus} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.focus) &&
                                        researchStudyResourceState.focus.map((focus, focusIndex) => {
                                            return <div key={focusIndex}><b>Focus: </b><DisplayFromFHIRCodeableReference codeableReference={focus} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept' deletable={true}
                                        elementName='condition' fieldLabel='Condition' startCollapsed
                                        startingValue={researchStudyResourceState.condition} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.condition) &&
                                        researchStudyResourceState.condition.map((condition, conditionIndex) => {
                                            return <div key={conditionIndex}><b>Condition: </b><DisplayFromFHIRCodeableConcept codeableConcept={condition} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept' deletable={true}
                                        elementName='keyword' fieldLabel='Keyword' startCollapsed
                                        startingValue={researchStudyResourceState.keyword} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.keyword) &&
                                        researchStudyResourceState.keyword.map((keyword, keywordIndex) => {
                                            return <div key={keywordIndex}><b>Keyword: </b><DisplayFromFHIRCodeableConcept codeableConcept={keyword} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept' deletable={true}
                                        elementName='classifier' fieldLabel='Classifier' startCollapsed
                                        startingValue={researchStudyResourceState.classifier} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.classifier) &&
                                        researchStudyResourceState.classifier.map((classifier, classifierIndex) => {
                                            return <div key={classifierIndex}><b>Classifier: </b><DisplayFromFHIRCodeableConcept codeableConcept={classifier} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept'
                                        elementName='region' fieldLabel='Region' startCollapsed
                                        startingValue={researchStudyResourceState.region} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.region) &&
                                        researchStudyResourceState.region.map((region, regionIndex) => {
                                            return <div key={regionIndex}><b>Region: </b><DisplayFromFHIRCodeableConcept codeableConcept={region} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="protocol">Protocol</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {(isM11ResearchStudyProfile || isStudyDesignProfile) && <StudyAmendmentEntry
                                        elementName='studyAmendment'
                                        fieldLabel='Study Amendment'
                                        startingValue={extensionState.studyAmendment} startCollapsed
                                        setResourceState={setExtensionState} />}
                                </>
                                :
                                <>
                                    {extensionState.studyAmendment && <div>
                                        <h4>Study Amendment</h4>
                                        <div style={{ marginLeft: "24px" }}>
                                            {extensionState.studyAmendment.details && <p><b>Details: </b>{extensionState.studyAmendment.details}</p>}
                                            {extensionState.studyAmendment.scope && <p><b>Scope: </b>{extensionState.studyAmendment.scope}</p>}
                                            {extensionState.studyAmendment.description && <p><b>Description: </b>{extensionState.studyAmendment.description}</p>}
                                            {extensionState.studyAmendment.rationale && <p><b>Rationale: </b>{extensionState.studyAmendment.rationale}</p>}
                                            {extensionState.studyAmendment.substantialImpactSafety === true && <p><b>Substantial Impact on Safety: </b>YES</p>}
                                            {extensionState.studyAmendment.substantialImpactSafety === false && <p><b>Substantial Impact on Safety: </b>No</p>}
                                            {extensionState.studyAmendment.substantialImpactSafetyComment && <p><b>Substantial Impact on Safety Comment: </b>{extensionState.studyAmendment.substantialImpactSafetyComment}</p>}
                                            {extensionState.studyAmendment.substantialImpactReliability === true && <p><b>Substantial Impact on Reliability: </b>YES</p>}
                                            {extensionState.studyAmendment.substantialImpactReliability === false && <p><b>Substantial Impact on Reliability: </b>No</p>}
                                            {extensionState.studyAmendment.substantialImpactReliabilityComment && <p><b>Substantial Impact on Reliability Comment: </b>{extensionState.studyAmendment.substantialImpactReliabilityComment}</p>}
                                            {extensionState.studyAmendment.primaryReason && <p><b>Primary Reason: </b>{displayStringFromFHIRCodeableConcept(extensionState.studyAmendment.primaryReason)}</p>}
                                        </div>
                                    </div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile && <>
                                        <ReferenceEntry
                                            elementName='systematicReviewEligibilityCriteria'
                                            fieldLabel='Systematic Review Eligibility Criteria'
                                            enableCreation startCollapsed startingResourceType="Group"
                                            referencedResourceTypes={['Group']} setProfile="SystematicReviewEligibilityCriteria"
                                            startingValue={extensionState.systematicReviewEligibilityCriteria}
                                            setResourceState={setExtensionState} />
                                    </>}
                                </>
                                :
                                <>
                                    {extensionState.systematicReviewEligibilityCriteria && <div>
                                        <b>Systematic Review Eligibility Criteria: </b><DisplayFromFHIRReference reference={extensionState.systematicReviewEligibilityCriteria} /></div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile && <>
                                        <ArrayEntry datatype='Reference'
                                            elementName='searchStrategy'
                                            fieldLabel='Search Strategy' enableCreation startCollapsed
                                            startingResourceType="Group"
                                            referencedResourceTypes={['Group']} setProfile="SearchStrategy"
                                            startingValue={extensionState.searchStrategy}
                                            setResourceState={setExtensionState} />
                                    </>}
                                </>
                                :
                                <>
                                    {Array.isArray(extensionState.searchStrategy) &&
                                        extensionState.searchStrategy.map((protocol, protocolIndex) => {
                                            return <div key={protocolIndex}><b>Search Strategy: </b><DisplayFromFHIRReference reference={protocol} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {!isSystematicReviewProfile && <CodeableConceptEntry elementName='phase' fieldLabel='Phase'
                                        valueSet={researchStudyDotPhaseValueSet} startCollapsed
                                        startingValue={researchStudyResourceState.phase} setResourceState={setResearchStudyResourceState} />}
                                </>
                                :
                                <>
                                    {researchStudyResourceState.phase &&
                                        <span><b>Phase: </b><DisplayFromFHIRCodeableConcept codeableConcept={researchStudyResourceState.phase} /><br /></span>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept'
                                        elementName='studyDesign' fieldLabel='Study Design'
                                        valueSet={SEVCO.studyDesign} startCollapsed codeableConceptLevelValueSet={studyDesignCodeableConceptSet}
                                        startingValue={researchStudyResourceState.studyDesign} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.studyDesign) &&
                                        researchStudyResourceState.studyDesign.map((studyDesign, studyDesignIndex) => {
                                            return <div key={studyDesignIndex}><b>Study Design: </b><DisplayFromFHIRCodeableConcept codeableConcept={studyDesign} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Reference'
                                        elementName='protocol' fieldLabel='Protocol' startCollapsed
                                        referencedResourceTypes={['PlanDefinition']}
                                        startingValue={researchStudyResourceState.protocol} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.protocol) &&
                                        researchStudyResourceState.protocol.map((protocol, protocolIndex) => {
                                            return <div key={protocolIndex}><b>Protocol: </b><DisplayFromFHIRReference reference={protocol} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {(isM11ResearchStudyProfile || isStudyDesignProfile) && <StringEntry
                                        elementName='saeReportingMethod'
                                        fieldLabel='Serious Adverse Event Reporting Method'
                                        startingValue={extensionState.saeReportingMethod}
                                        setResourceState={setExtensionState} />}
                                </>
                                :
                                <>
                                    {extensionState.saeReportingMethod &&
                                        <p><b>Serious Adverse Event Reporting Method: </b>{extensionState.saeReportingMethod}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='RelatesTo' startCollapsed
                                        elementName='relatesTo' fieldLabel='Related Item' allowedTypeValues="FHIR"
                                        startingValue={researchStudyResourceState.relatesTo} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.relatedArtifact) &&
                                        researchStudyResourceState.relatedArtifact.map((relatedArtifact, relatedArtifactIndex) => {
                                            return <div key={relatedArtifactIndex}><b>Related Item: </b><DisplayFromFHIRRelatedArtifact relatedArtifact={relatedArtifact} /></div>
                                        })}
                                    {Array.isArray(researchStudyResourceState.relatesTo) &&
                                        researchStudyResourceState.relatesTo.map((relatedArtifact, relatedArtifactIndex) => {
                                            return <div key={relatedArtifactIndex}><b>Related Item: </b><DisplayFromFHIRRelatesTo relatesTo={relatedArtifact} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Annotation'
                                        elementName='note' fieldLabel='Note' startCollapsed
                                        startingValue={researchStudyResourceState.note} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.note) &&
                                        researchStudyResourceState.note.map((note, noteIndex) => {
                                            return <div key={noteIndex}><b>Note: </b><DisplayFromFHIRAnnotation annotation={note} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="organizations">Organizations</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyAssociatedParty'
                                        elementName='associatedParty' fieldLabel='Associated Party' startCollapsed
                                        startingValue={researchStudyResourceState.associatedParty} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.associatedParty) &&
                                        researchStudyResourceState.associatedParty.map((party, partyIndex) => {
                                            return <div key={partyIndex}><b>Associated Party: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {party.name && <p><b>Name: </b>{party.name}</p>}
                                                    {party.role && <span><b>Role: </b><DisplayFromFHIRCodeableConcept codeableConcept={party.role} /><br /></span>}
                                                    {Array.isArray(party.period) &&
                                                        party.period.map((period, periodIndex) => {
                                                            return <div key={periodIndex}><b>Period: </b>{displayStringFromFHIRPeriod(period)}</div>
                                                        })}
                                                    {Array.isArray(party.classifier) &&
                                                        party.classifier.map((classifier, classifierIndex) => {
                                                            return <div key={classifierIndex}><b>Classifier: </b><DisplayFromFHIRCodeableConcept codeableConcept={classifier} /></div>
                                                        })}
                                                    {party.party && <div><b>Party: </b><DisplayFromFHIRReference reference={party.party} /><br /></div>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Reference'
                                        elementName='site' fieldLabel='Site' enableCreation={true} startCollapsed
                                        referencedResourceTypes={siteReferenceResourceTypes}
                                        startingValue={researchStudyResourceState.site} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.site) &&
                                        researchStudyResourceState.site.map((siteInstance, siteIndex) => {
                                            return <div key={siteIndex}><b>Site: </b><DisplayFromFHIRReference reference={siteInstance} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="recruitment">Recruitment</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile ?
                                        <>This section is not relevant for a systematic review.</>
                                        :
                                        <ResearchStudyRecruitmentEntry elementName='recruitment' fieldLabel='Recruitment' startCollapsed
                                            startingValue={researchStudyResourceState.recruitment} setResourceState={setResearchStudyResourceState} />
                                    }
                                </>
                                :
                                <>
                                    {researchStudyResourceState.recruitment?.targetNumber && <p><b>Target number of participants: </b>{researchStudyResourceState.recruitment.targetNumber}</p>}
                                    {(researchStudyResourceState.recruitment?.actualNumber || researchStudyResourceState.recruitment?.actualNumber === 0) && <p><b>Actual number of participants: </b>{researchStudyResourceState.recruitment.actualNumber}</p>}
                                    {researchStudyResourceState.recruitment?.eligibility && <div><b>Eligibility Criteria: </b><DisplayFromFHIRReference reference={researchStudyResourceState.recruitment.eligibility} /><br /></div>}
                                    {researchStudyResourceState.recruitment?.actualGroup && <div><b>Enrollment Group: </b><DisplayFromFHIRReference reference={researchStudyResourceState.recruitment.actualGroup} /><br /></div>}
                                    {!researchStudyResourceState.recruitment && <p>No recruitment data.</p>}
                                </>
                        }
                    </div>
                    <h3 id="variables">Variables</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyComparisonGroup'
                                        elementName='comparisonGroup' fieldLabel='Comparison Group' startCollapsed
                                        startingValue={researchStudyResourceState.comparisonGroup}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.comparisonGroup) &&
                                        researchStudyResourceState.comparisonGroup.map((exposure, exposureIndex) => {
                                            return <div key={exposureIndex}><b>Comparison Group: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {exposure.linkId && <p><b>Id: </b>{exposure.linkId}</p>}
                                                    {exposure.name && <p><b>Name: </b>{exposure.name}</p>}
                                                    {exposure.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={exposure.type} /><br /></span>}
                                                    {exposure.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={exposure.description} /><br /><br /></div>}
                                                    {exposure.targetNumber && <p><b>Target number of participants: </b>{exposure.targetNumber}</p>}
                                                    {(exposure.actualNumber || exposure.actualNumber === 0) && <p><b>Actual number of participants: </b>{exposure.actualNumber}</p>}
                                                    {exposure.eligibility && <div><b>Eligibility Criteria: </b><DisplayFromFHIRReference reference={exposure.eligibility} /><br /></div>}
                                                    {Array.isArray(exposure.intendedExposure) &&
                                                        exposure.intendedExposure.map((intended, intendedIndex) => {
                                                            return <div key={intendedIndex}><b>Exposure: </b><DisplayFromFHIRReference reference={intended} /></div>
                                                        })}
                                                    {exposure.observedGroup && <span><b>Observed Group: </b><DisplayFromFHIRReference reference={exposure.observedGroup} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyObjective'
                                        elementName='objective' fieldLabel='Objective' startCollapsed
                                        startingValue={researchStudyResourceState.objective} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.objective) &&
                                        researchStudyResourceState.objective.map((objective, objectiveIndex) => {
                                            return <div key={objectiveIndex}><b>Objective: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {objective.name && <p><b>Name: </b>{objective.name}</p>}
                                                    {objective.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={objective.type} /><br /></span>}
                                                    {objective.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={objective.description} /><br /><br /></div>}
                                                    {Array.isArray(objective.outcomeMeasure) &&
                                                        objective.outcomeMeasure.map((outcomeMeasure, outcomeMeasureIndex) => {
                                                            let name = outcomeMeasure.name || "";
                                                            let type = outcomeMeasure.type || "";
                                                            let description = outcomeMeasure.description || "";
                                                            let endpoint = outcomeMeasure.endpoint || "";
                                                            let population = outcomeMeasure.population || "";
                                                            let intervention = outcomeMeasure.intervention || "";
                                                            let comparator = outcomeMeasure.comparator || "";
                                                            let summaryMeasure = outcomeMeasure.summaryMeasure || "";
                                                            let eventHandling = outcomeMeasure.eventHandling || [];
                                                            return <div key={outcomeMeasureIndex} >
                                                                <h4>Outcome Measure {outcomeMeasureIndex + 1}</h4>
                                                                <div style={{ marginLeft: "24px" }}>
                                                                    {name && <p><b>Name: </b>{name}</p>}
                                                                    {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                                    {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                                                    {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                                                    {intervention && <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={intervention} /><br /></span>}
                                                                    {comparator && <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={comparator} /><br /></span>}
                                                                    {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                                                    {Array.isArray(eventHandling) &&
                                                                        eventHandling.map((ice, iceIndex) => {
                                                                            let event = ice.event || "";
                                                                            let group = ice.group || "";
                                                                            let handling = ice.handling || "";
                                                                            let description = ice.description || "";
                                                                            return <div key={iceIndex}>
                                                                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                                                <div style={{ marginLeft: "24px" }}>
                                                                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                                                </div>
                                                                            </div>
                                                                        })}
                                                                </div>
                                                            </div>
                                                        })}
                                                    {Array.isArray(objective.extension) &&
                                                        objective.extension.map((extension, extensionIndex) => {
                                                            if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand") {
                                                                let label;
                                                                let type
                                                                let endpoint;
                                                                let population;
                                                                let intervention;
                                                                let comparator;
                                                                let summaryMeasure;
                                                                let eventHandling = [];
                                                                for (const ext of extension.extension) {
                                                                    if (ext.url === "label") {
                                                                        label = ext.valueString;
                                                                    } else if (ext.url === "type") {
                                                                        type = ext.valueCodeableConcept;
                                                                    } else if (ext.url === "endpoint") {
                                                                        endpoint = ext.valueReference;
                                                                    } else if (ext.url === "population") {
                                                                        population = ext.valueReference;
                                                                    } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                                                                        intervention = ext.valueId;
                                                                    } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                                                                        comparator = ext.valueId;
                                                                    } else if (ext.url === "summaryMeasure") {
                                                                        summaryMeasure = ext.valueCodeableConcept;
                                                                    } else if (ext.url === "eventHandling") {
                                                                        eventHandling.push(ext);
                                                                    }
                                                                }
                                                                return <div key={extensionIndex} >
                                                                    <h4>Estimand {extensionIndex + 1}</h4>
                                                                    <div style={{ marginLeft: "24px" }}>
                                                                        {label && <p><b>Label: </b>{label}</p>}
                                                                        {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                                                        {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                                                        {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                                                        {intervention && <p><b>Intervention group: </b>Comparison group {intervention}</p>}
                                                                        {comparator && <p><b>Comparator group: </b>Comparison group {comparator}</p>}
                                                                        {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                                                        {Array.isArray(eventHandling) &&
                                                                            eventHandling.map((ice, iceIndex) => {
                                                                                let event;
                                                                                let group;
                                                                                let handling;
                                                                                let description;
                                                                                for (const extension of ice.extension) {
                                                                                    if (extension.url === "event") {
                                                                                        event = extension.valueCodeableConcept;
                                                                                    } else if (extension.url === "group") {
                                                                                        group = extension.valueCodeableConcept;
                                                                                    } else if (extension.url === "handling") {
                                                                                        handling = extension.valueCodeableConcept;
                                                                                    } else if (extension.url === "description") {
                                                                                        description = extension.valueMarkdown;
                                                                                    }
                                                                                }
                                                                                return <div key={iceIndex}>
                                                                                    <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                                                    <div style={{ marginLeft: "24px" }}>
                                                                                        {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                                                        {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                                                        {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                                                        {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                                                    </div>
                                                                                </div>
                                                                            })}
                                                                    </div>
                                                                </div>
                                                            } else {
                                                                return <></>;
                                                            }
                                                        })}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyOutcomeMeasure'
                                        elementName='outcomeMeasure' fieldLabel='Outcome Measure (pre-2025 FHIR)' startCollapsed
                                        startingValue={researchStudyResourceState.outcomeMeasure} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.outcomeMeasure) &&
                                        researchStudyResourceState.outcomeMeasure.map((outcome, outcomeIndex) => {
                                            return <div key={outcomeIndex}><b>Outcome Measure: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {outcome.name && <p><b>Name: </b>{outcome.name}</p>}
                                                    {Array.isArray(outcome.type) &&
                                                        outcome.type.map((type, typeIndex) => {
                                                            return <div key={typeIndex}><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /></div>
                                                        })}
                                                    {outcome.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={outcome.description} /><br /><br /></div>}
                                                    {outcome.reference && <span><b>Outcome Definition: </b><DisplayFromFHIRReference reference={outcome.reference} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="results">Results</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile && <>
                                        <UnsignedIntEntry
                                            elementName='numberOfStudiesIdentified'
                                            fieldLabel='Number of studies identified'
                                            startingValue={extensionState.numberOfStudiesIdentified}
                                            setResourceState={setExtensionState} />
                                        <UnsignedIntEntry
                                            elementName='numberOfStudiesIncluded'
                                            fieldLabel='Number of studies included'
                                            startingValue={extensionState.numberOfStudiesIncluded}
                                            setResourceState={setExtensionState} />
                                        <ArrayEntry datatype='Reference'
                                            elementName='includedStudy'
                                            fieldLabel='Included study'
                                            referencedResourceTypes={['Citation', 'Composition', 'ResearchStudy']}
                                            enableCreation={true} startCollapsed
                                            startingValue={extensionState.includedStudy}
                                            setResourceState={setExtensionState} />
                                        <ArrayEntry datatype='Reference'
                                            elementName='excludedStudy'
                                            fieldLabel='Excluded study'
                                            referencedResourceTypes={['Citation', 'Composition', 'ResearchStudy']}
                                            enableCreation={true} startCollapsed
                                            startingValue={extensionState.excludedStudy}
                                            setResourceState={setExtensionState} />
                                    </>}
                                    <ArrayEntry datatype='Reference' elementName='result'
                                        fieldLabel='Result (as Reference)'
                                        startCollapsed
                                        referencedResourceTypes={resultReferenceResourceTypes} enableCreation={true}
                                        startingValue={researchStudyResourceState.result || null} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {
                                        (Array.isArray(researchStudyResourceState.result) || !isNaN(extensionState.numberOfStudiesIdentified) ||
                                            !isNaN(extensionState.numberOfStudiesIncluded) || extensionState.includedStudy?.length || extensionState.excludedStudy?.length) ? <>
                                            {!isNaN(extensionState.numberOfStudiesIdentified) &&
                                                <p><b>Number of studies identified: </b>{extensionState.numberOfStudiesIdentified}</p>}
                                            {!isNaN(extensionState.numberOfStudiesIncluded) &&
                                                <p><b>Number of studies included: </b>{extensionState.numberOfStudiesIncluded}</p>}
                                            {Array.isArray(extensionState.includedStudy) &&
                                                extensionState.includedStudy.map((result, resultIndex) => {
                                                    return <div key={resultIndex}><b>Included study: </b><DisplayFromFHIRReference reference={result} /></div>
                                                })}
                                            {Array.isArray(extensionState.excludedStudy) &&
                                                extensionState.excludedStudy.map((result, resultIndex) => {
                                                    return <div key={resultIndex}><b>Excluded study: </b><DisplayFromFHIRReference reference={result} /></div>
                                                })}
                                            {Array.isArray(researchStudyResourceState.result) &&
                                                researchStudyResourceState.result.map((result, resultIndex) => {
                                                    return <div key={resultIndex}><b>Result: </b><DisplayFromFHIRReference reference={result} /></div>
                                                })}
                                        </>
                                            :
                                            <p>No results</p>
                                    }
                                </>
                        }
                    </div>
                </div>
            </div>
        </>}
    </div>
}

export default ResearchStudyDisplay;
export {
    ResearchStudyAssociatedPartyEntry, ResearchStudyComparisonGroupEntry, ResearchStudyLabelEntry,
    EventHandlingEntry, StudyAmendmentEntry, EstimandEntry, ResearchStudyObjectiveEntry,
    ResearchStudyObjectiveOutcomeMeasureEntry, ResearchStudyOutcomeMeasureEntry, ResearchStudyProgressStatusEntry,
    ResearchStudyRecruitmentEntry, ResearchStudyRegistrationExtensionEntry
};